import "./lib/addressRequestToken";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { initializePushNotifications, addPushNotificationListeners } from "./utils/pushNotifications";
import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { restoreNativeAuthSession } from "./utils/nativeAuthStorage";
import { isStaleChunkError, tryStaleChunkReload } from "./lib/staleChunkReload";

// Auto-recover from stale chunk hashes after a new deploy.
// When the browser tries to load an old code-split chunk that no longer
// exists, force a one-time hard reload so it fetches the fresh index.html.
// The guard is shared with ErrorBoundary (src/lib/staleChunkReload.ts).
if (typeof window !== "undefined") {
  // Vite fires this when a lazy page chunk from an older deploy is gone. Only swallow the error when we
  // actually reload; otherwise Vite rethrows the original import error so the page can show it.
  window.addEventListener("vite:preloadError", (e) => {
    if (tryStaleChunkReload()) e.preventDefault();
  });

  // These only observe: the original error or rejection is never cancelled.
  window.addEventListener("error", (e) => {
    if (isStaleChunkError(e?.error ?? e?.message)) tryStaleChunkReload();
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (isStaleChunkError(e?.reason)) tryStaleChunkReload();
  });
}

const bootstrap = async () => {
  // On native iOS/Android, restore Supabase auth tokens from native
  // Preferences into localStorage BEFORE React mounts and the Supabase
  // client reads its session. This prevents the WebView from logging the
  // user out when the app is backgrounded.
  await restoreNativeAuthSession();

  if (typeof window !== "undefined") {
    // Install registration listeners before requesting a token: native callbacks
    // can arrive immediately when permission has already been granted.
    void addPushNotificationListeners()
      .then(() => initializePushNotifications())
      .catch(console.error);
  }

  createRoot(document.getElementById("root")!).render(<App />);

  // Hide the native splash screen once React has mounted and painted the first frame.
  if (Capacitor.isNativePlatform()) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        SplashScreen.hide({ fadeOutDuration: 250 }).catch(() => {
          // Plugin may not be available; safe to ignore
        });
      });
    });
  }
};

bootstrap();
